// Dashboard.jsx — aligned tables (colgroup), heatmap fills card height; equal halves; donut separator
import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';
import api from '../api';

const fmtINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat('en-IN').format(n || 0);
const DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const SHOP_STATE = (process.env.REACT_APP_SHOP_STATE || 'OD').toUpperCase();

const toNum = (v) => (v == null ? 0 : typeof v === 'number' ? v : parseFloat(String(v)) || 0);
const dateKey = (iso) => dayjs(iso).format('YYYY-MM-DD');

function Heatmap({ matrix }){
  const max = Math.max(1, ...matrix.flat());
  return (
    <div className="heatmap">
      <div className="hm-grid">
        <div />
        <div className="hm-x">
          {Array.from({length:24},(_,h)=>(<div key={h} className="hm-xlab">{String(h).padStart(2,'0')}</div>))}
        </div>
        {matrix.map((row,r)=>(
          <React.Fragment key={r}>
            <div className="hm-ylab">{DAY_LABELS[r]}</div>
            <div className="hm-row">
              {row.map((v,c)=>{
                const t = v/max;
                const bg = `hsl(220 90% ${95 - Math.round(t*50)}%)`;
                return <div key={c} className="hm-cell" title={`${DAY_LABELS[r]} ${String(c).padStart(2,'0')}:00: ${fmtINR(v)}`} style={{background:bg}}/>;
              })}
            </div>
          </React.Fragment>
        ))}
      </div>
      <div className="hm-legend"><span>Low</span><div className="hm-grad"/><span>High</span></div>
    </div>
  );
}

export default function Dashboard(){
  const today = dayjs().format('YYYY-MM-DD');
  const start30 = dayjs().subtract(29,'day').format('YYYY-MM-DD');
  const [filters, setFilters] = useState({ from: start30, to: today });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    kpis:{ sales_today:0, sales_mtd:0, aov:0, gst_total:0, gst_igst:0, gst_cgst:0, gst_sgst:0, customers_new:0, paid_cash:0, paid_upi:0, paid_card:0 },
    timeseries:[], customer_growth:[], top_products:[], top_customers:[], sales_by_state:[], hourly_sales:Array.from({length:7},()=>Array(24).fill(0)),
    returns_top_products: [],
    returns_top_customers: [],
    top_credit_customers: [],
    adjustments: { discount:0, credit:0 }
  });

  const [now, setNow] = useState(dayjs());
  useEffect(() => {
    const t = setInterval(() => setNow(dayjs()), 1000);
    return () => clearInterval(t);
  }, []);

  // Fetch analytics for given date range
  const fetchAnalytics = async (p) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ from: p.from, to: p.to, limit: 'all' }).toString();

      // In-range sales
      const salesRes = await api.get(`/sales?${qs}`);
      const sales = Array.isArray(salesRes.data) ? salesRes.data : [];

      // Lifetime context: sales BEFORE the range (to classify Returning vs New)
      let prevSales = [];
      try {
        const toPrev = dayjs(p.from).subtract(1, 'day').format('YYYY-MM-DD'); // strictly before 'from'
        const prevRes = await api.get(`/sales?to=${toPrev}&limit=all`);
        prevSales = Array.isArray(prevRes.data) ? prevRes.data : [];
      } catch {}

      // GST
      const repRes = await api.get(`/reports/sales?from=${p.from}&to=${p.to}`);
      const gst = repRes.data || {};

      // Returns summary
      let retTopProducts = [];
      let retTopCustomers = [];
      try {
        const retRes = await api.get(`/returns/summary?from=${p.from}&to=${p.to}`);
        retTopProducts  = retRes.data?.top_return_products  || [];
        retTopCustomers = retRes.data?.top_return_customers || [];
      } catch {}

      // Top customers by store credit
      let topCreditCustomers = [];
      try {
        const custRes = await api.get('/customers');
        const customers = Array.isArray(custRes.data) ? custRes.data : [];
        topCreditCustomers = customers
          .map(c => ({ id:c.id, name:c.name || '—', phone:c.phone, credit:Number(c.store_credit||0) }))
          .filter(c => c.credit > 0)
          .sort((a,b)=>b.credit-a.credit)
          .slice(0,10);
      } catch {}

      // ── Aggregations ───────────────────────────────────────────────────────────
      const hourly = Array.from({length:7},()=>Array(24).fill(0));
      const byDay = new Map();
      const byState = new Map();
      const productsAgg = new Map();
      const customerAgg = new Map(); // { name, phone, orders, revenue, last }
      const customerFirstSeen = new Map(); // within in-range dataset (used elsewhere)

      // For growth classification
      const seenBeforeRange = new Set( prevSales.map(s => s.customer_id).filter(Boolean) ); // lifetime-before-range customers
      const dayCustomers = new Map();   // dayKey -> Set(customer_id) who ordered that day (in-range)
      const firstInRange  = new Map();  // customer_id -> first dayKey inside the range

      let totalRevenue=0, totalDiscount=0, totalCreditApplied=0;

      for(const s of sales){
        const created_at = s.created_at || s.sold_at;
        if(!created_at) continue;

        const key  = dateKey(created_at);
        const d    = dayjs(created_at);
        const dow  = d.day();
        const hour = d.hour();

        const revenue = toNum(s.total);
        totalRevenue += revenue;

        // adjustments
        totalDiscount       += toNum(s.discount);
        totalCreditApplied  += toNum(s.credit_applied);

        // by day
        if(!byDay.has(key)) byDay.set(key,{sales:0,invoices:0,customers:new Set(),paid_cash:0,paid_upi:0,paid_card:0});
        const db = byDay.get(key);
        db.sales += revenue; db.invoices += 1; db.customers.add(s.customer_id);

        // payment mix
        const pm = String(s.payment_method||'').toLowerCase();
        if(pm.includes('cash')) db.paid_cash += revenue;
        else if(pm.includes('upi')) db.paid_upi += revenue;
        else db.paid_card += revenue;

        // state mix
        const st = (s.customer_state_code || s.state_code || s.customer_state || s.state || '').toString().toUpperCase();
        if(st) byState.set(st, (byState.get(st)||0) + revenue);

        // hourly heatmap
        if(dow>=0 && hour>=0) hourly[dow][hour] += revenue;

        // track earliest seen (within in-range dataset)
        if(!customerFirstSeen.has(s.customer_id)) customerFirstSeen.set(s.customer_id, key);

        // growth: unique customers per day & first in-range day
        if (!dayCustomers.has(key)) dayCustomers.set(key, new Set());
        dayCustomers.get(key).add(s.customer_id);
        if (!firstInRange.has(s.customer_id)) firstInRange.set(s.customer_id, key);

        // customer + product aggregates
        const cname =
          s.customer_name || s.customerName || (s.customer && (s.customer.name || s.customer.full_name)) ||
          s.name || `Customer #${s.customer_id}`;
        const cphone =
          s.customer_phone || (s.customer && s.customer.phone) || s.phone || '';

        const existing = customerAgg.get(s.customer_id) || {
          name: cname, phone: cphone || '', orders: 0, revenue: 0, last: created_at
        };
        existing.orders  += 1;
        existing.revenue += revenue;
        existing.last     = dayjs(existing.last).isAfter(d) ? existing.last : d.toISOString();
        if (cname && (/^Customer #/.test(existing.name) || !existing.name)) existing.name = cname;
        if (!existing.phone && cphone) existing.phone = cphone;
        customerAgg.set(s.customer_id, existing);

        if(Array.isArray(s.items)){
          for(const it of s.items){
            const id = it.product_id ?? it.sku ?? it.name;
            const prev = productsAgg.get(id) || { name: it.name, qty:0, revenue:0 };
            prev.qty     += toNum(it.qty);
            prev.revenue += toNum(it.qty)*toNum(it.price);
            productsAgg.set(id, prev);
          }
        }
      }

      // Timeseries for charts
      const daysSorted = Array.from(byDay.keys()).sort();
      const timeseries = daysSorted.map(k=>{
        const b = byDay.get(k);
        return {
          fullDate: k,
          date: dayjs(k).format('DD-MMM'),
          sales: Math.round(b.sales),
          invoices: b.invoices,
          customers: b.customers.size,
          paid_cash: b.paid_cash,
          paid_upi:  b.paid_upi,
          paid_card: b.paid_card
        };
      });

      // ── Customer Growth per your definition ──────────────────────────────────
      const customer_growth = daysSorted.map(k => {
        const todays = dayCustomers.get(k) || new Set();
        let newC = 0, returning = 0;

        todays.forEach(cid => {
          if (seenBeforeRange.has(cid)) {
            returning += 1;                  // purchased before the range
          } else if (firstInRange.get(cid) === k) {
            newC += 1;                       // lifetime first purchase today (inside the range)
          } else {
            returning += 1;                  // first in-range day earlier → returning today
          }
        });

        return { date: dayjs(k).format('DD-MMM'), new_customers: newC, returning_customers: returning };
      });

      // Other aggregates
      const top_products = Array.from(productsAgg.values())
        .sort((a,b)=>b.revenue-a.revenue)
        .slice(0,10);

      const top_customers = Array.from(customerAgg.values())
        .map(v=>({
          name: v.name || '—',
          phone: v.phone || '',
          orders: v.orders,
          revenue: Math.round(v.revenue),
          last_purchase: dayjs(v.last).format('DD-MMM-YYYY')
        }))
        .sort((a,b)=>b.revenue-a.revenue)
        .slice(0,10);

      const sales_by_state = Array.from(byState.entries())
        .map(([state,revenue])=>({ state, revenue: Math.round(revenue), txType: state===SHOP_STATE ? 'CGST/SGST':'IGST' }));

      const invoicesCount = sales.length;
      const aov = Math.round((totalRevenue) / Math.max(1, invoicesCount));
      const todayKey = dateKey(dayjs());
      const sales_today = Math.round(byDay.get(todayKey)?.sales || 0);

      const kpis = {
        sales_today,
        sales_mtd: Math.round(totalRevenue),
        aov,
        gst_total: toNum(gst.cgst)+toNum(gst.sgst)+toNum(gst.igst),
        gst_igst: toNum(gst.igst),
        gst_cgst: toNum(gst.cgst),
        gst_sgst: toNum(gst.sgst),
        customers_new: customerFirstSeen.size,
        paid_cash: Math.round(timeseries.reduce((a,b)=>a+b.paid_cash,0)),
        paid_upi:  Math.round(timeseries.reduce((a,b)=>a+b.paid_upi,0)),
        paid_card: Math.round(timeseries.reduce((a,b)=>a+b.paid_card,0)),
      };

      setData({
        kpis,
        timeseries,
        customer_growth,
        top_products,
        top_customers,
        sales_by_state,
        hourly_sales: hourly,
        returns_top_products: retTopProducts.map(r=>({
          name: r.name, qty: Number(r.qty_returned||0), amount: Math.round(r.amount_abs||0)
        })),
        returns_top_customers: retTopCustomers.map(r=>{
          const last = r.last_return || r.last_return_at || r.latest_return_at;
          return {
            customer: r.customer_name || '—',
            phone: r.customer_phone || '',
            returns: r.returns_count || r.count || 0,
            amount: Math.round(r.amount_abs || r.total_amount_abs || 0),
            last_return: last ? dayjs(last).format('DD-MMM-YYYY') : '—'
          };
        }),
        top_credit_customers: topCreditCustomers,
        adjustments: { discount: Math.round(totalDiscount), credit: Math.round(totalCreditApplied) }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(()=>{ fetchAnalytics(filters); /* eslint-disable-next-line */ },[]);

  const gstPieData = useMemo(()=>[
    { name:'IGST', value: data.kpis.gst_igst||0 },
    { name:'CGST', value: data.kpis.gst_cgst||0 },
    { name:'SGST', value: data.kpis.gst_sgst||0 },
  ],[data.kpis]);

  const adjustmentsPie = useMemo(()=>[
    { name:'Discount', value: data.adjustments.discount||0 },
    { name:'Credit', value: data.adjustments.credit||0 },
  ],[data.adjustments]);

  const weekdayPerformance = useMemo(()=>{
    const buckets = Array.from({length:7},()=>0);
    data.timeseries.forEach(t=>{
      const dow = dayjs(t.fullDate).day();
      buckets[dow] += t.sales||0;
    });
    return buckets.map((v,i)=>({ day: DAY_LABELS[i], sales: v }));
  },[data.timeseries]);

  const busiest = useMemo(()=>{
    if(!weekdayPerformance.length) return { day:'-', sales:0 };
    return weekdayPerformance.reduce((m,r)=>r.sales>m.sales? r:m, weekdayPerformance[0]);
  },[weekdayPerformance]);

  const onQuick = (days) => {
    const to = dayjs().format('YYYY-MM-DD');
    const from = dayjs().subtract(days-1,'day').format('YYYY-MM-DD');
    const next = { ...filters, from, to };
    setFilters(next);
    fetchAnalytics(next);
  };

  const totalAdjustments = (data.adjustments.discount||0) + (data.adjustments.credit||0);
  const pctAdj = (v) => ((v/Math.max(1,totalAdjustments))*100).toFixed(1);

  return (
    <div className="page">
      {/* Header */}
      <div className="row between">
        <div className="head-left">
          <h2>Analytics Dashboard</h2>
          <div className="subhead">
            Welcome back 👋 — Today is {now.format('dddd, DD-MMM-YYYY , hh:mm A')}
          </div>
        </div>

        {/* Controls */}
        <div className="row gap8 controls">
          <input type="date" value={filters.from} onChange={e=>{ const next={...filters, from:e.target.value}; setFilters(next); fetchAnalytics(next); }} />
          <input type="date" value={filters.to} onChange={e=>{ const next={...filters, to:e.target.value}; setFilters(next); fetchAnalytics(next); }} />
          <button type="button" className="btn" onClick={()=>onQuick(7)}>Last 7d</button>
          <button type="button" className="btn" onClick={()=>onQuick(30)}>Last 30d</button>
          <button type="button" className="btn" onClick={()=>onQuick(90)}>Last 90d</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid kpi-grid">
        <div className="kpi kpi-1"><div className="label">Today's Sales</div><div className="big">{fmtINR(data.kpis.sales_today)}</div><div className="meta">{dayjs().format('DD-MMM-YYYY')}</div></div>
        <div className="kpi kpi-2"><div className="label">MTD Sales</div><div className="big">{fmtINR(data.kpis.sales_mtd)}</div><div className="meta">{filters.from} → {filters.to}</div></div>
        <div className="kpi kpi-3"><div className="label">Average Order Value</div><div className="big">{fmtINR(data.kpis.aov)}</div></div>
        <div className="kpi kpi-4"><div className="label">New Customers</div><div className="big">{fmtNum(data.kpis.customers_new)}</div></div>
        <div className="kpi kpi-5"><div className="label">Paid by Cash</div><div className="big">{fmtINR(data.kpis.paid_cash)}</div></div>
        <div className="kpi kpi-6"><div className="label">Paid by UPI</div><div className="big">{fmtINR(data.kpis.paid_upi)}</div></div>
        <div className="kpi kpi-7"><div className="label">Paid by Card</div><div className="big">{fmtINR(data.kpis.paid_card)}</div></div>
        <div className="kpi kpi-8"><div className="label">GST Collected</div><div className="big">{fmtINR(data.kpis.gst_total)}</div><div className="meta">IGST + CGST + SGST</div></div>
      </div>

      {/* Charts row 1 — equal halves */}
      <div className="grid two-eq">
        <div className="card">
          <h3>Sales Trend</h3>
          <div className="h-300" style={{ minHeight: 260 }}>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.timeseries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v, n)=> n==='sales'? fmtINR(v): fmtNum(v)} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                <Legend />
                <Line type="monotone" dataKey="sales" name="Sales" stroke="#3b82f6" strokeWidth={3} dot={false} />
                <Line type="monotone" dataKey="invoices" name="Invoices" stroke="#10b981" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card">
          <div className="row between"><h3>Weekday Performance</h3><div className="badge">Peak: <b>{busiest.day}</b> {fmtINR(busiest.sales)}</div></div>
          <div className="h-300">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={weekdayPerformance}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="day" /><YAxis />
                <Tooltip formatter={(v)=>fmtINR(v)} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} /><Legend />
                <Bar dataKey="sales" name="Sales" fill="#3b82f6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts row 2 — equal halves; HEATMAP fills the box */}
      <div className="grid two-eq">
        <div className="card" style={{ overflowX:'auto' }}>
          <h3>Customer Growth Trend</h3>
          <div className="h-300" style={{ minHeight: 260, minWidth: 320 }}>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.customer_growth} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" /><YAxis /><Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} /><Legend />
                <Line type="monotone" dataKey="new_customers" name="New" stroke="#ef4444" strokeWidth={3} dot={false} />
                <Line type="monotone" dataKey="returning_customers" name="Returning" stroke="#10b981" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card">
          <h3>Hourly Heatmap (Weekday × Hour)</h3>
          <div className="h-300" style={{ minHeight: 260, overflowX:'auto' }}>
            <Heatmap matrix={data.hourly_sales} />
          </div>
        </div>
      </div>

      {/* Combined Donuts with vertical separator */}
      <div className="card">
        <div className="row between"><h3>Tax & Adjustments</h3></div>
        <div className="donut-grid">
          <div className="donut-wrap">
            <div className="donut-chart">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart margin={{ top: 6, right: 6, bottom: 6, left: 6 }}>
                  <Pie
                    dataKey="value"
                    data={gstPieData}
                    innerRadius={80}
                    outerRadius={110}
                    paddingAngle={1}
                    labelLine={false}
                    label={false}
                  >
                    <Cell fill="#3b82f6" /><Cell fill="#10b981" /><Cell fill="#8b5cf6" />
                  </Pie>
                  <text x="50%" y="42%" textAnchor="middle" dominantBaseline="middle" className="donut-total-top">Total GST</text>
                  <text x="50%" y="56%" textAnchor="middle" dominantBaseline="middle" className="donut-total">{fmtINR(data.kpis.gst_total)}</text>
                  <Legend layout="horizontal" verticalAlign="bottom" align="center" iconSize={12} wrapperStyle={{ fontSize: 12, marginTop: 6 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="gst-summary">
              <div><span className="muted">IGST</span><b>{fmtINR(data.kpis.gst_igst)} </b><span className="pct">
                ({((data.kpis.gst_igst/Math.max(1,data.kpis.gst_total))*100).toFixed(1)}%)</span></div>
              <div><span className="muted">CGST</span><b>{fmtINR(data.kpis.gst_cgst)} </b><span className="pct">
                ({((data.kpis.gst_cgst/Math.max(1,data.kpis.gst_total))*100).toFixed(1)}%)</span></div>
              <div><span className="muted">SGST</span><b>{fmtINR(data.kpis.gst_sgst)} </b><span className="pct">
                ({((data.kpis.gst_sgst/Math.max(1,data.kpis.gst_total))*100).toFixed(1)}%)</span></div>
            </div>
          </div>

          <div className="v-sep" aria-hidden />

          <div className="donut-wrap">
            <div className="donut-chart">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart margin={{ top: 6, right: 6, bottom: 6, left: 6 }}>
                  <Pie
                    dataKey="value"
                    data={adjustmentsPie}
                    innerRadius={80}
                    outerRadius={110}
                    paddingAngle={1}
                    labelLine={false}
                    label={false}
                  >
                    <Cell fill="#f59e0b" /><Cell fill="#ec4899" />
                  </Pie>
                  <text x="50%" y="42%" textAnchor="middle" dominantBaseline="middle" className="donut-total-top">Total Adjustments</text>
                  <text x="50%" y="56%" textAnchor="middle" dominantBaseline="middle" className="donut-total">
                    {fmtINR((data.adjustments.discount||0) + (data.adjustments.credit||0))}
                  </text>
                  <Legend layout="horizontal" verticalAlign="bottom" align="center" iconSize={12} wrapperStyle={{ fontSize: 12, marginTop: 6 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="gst-summary">
              <div><span className="muted">Discount</span><b>{fmtINR(data.adjustments.discount||0)} </b>
                <span className="pct">({pctAdj(data.adjustments.discount||0)}%)</span></div>
              <div><span className="muted">Credit</span><b>{fmtINR(data.adjustments.credit||0)} </b>
                <span className="pct">({pctAdj(data.adjustments.credit||0)}%)</span></div>
              <div className="only-center"><span className="muted">Total</span><b>{fmtINR(totalAdjustments)}</b></div>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue & Customers tables */}
      <div className="grid two-eq">
        <div className="card table-card">
          <h3>Top 10 Products Sales</h3>
          <div className="table-scroll">
            <table className="mini">
              <colgroup>
                <col className="w-flex" /><col className="w-96" /><col className="w-140" />
              </colgroup>
              <thead>
                <tr><th className="left">Product</th><th className="num">Qty</th><th className="money">Revenue</th></tr>
              </thead>
              <tbody>{data.top_products.length === 0 ? (
                <tr><td colSpan={3} className="muted">No line-item detail available for this range.</td></tr>
              ) : (
                data.top_products.map((r,i)=>(
                  <tr key={i}><td className="left">{r.name}</td><td className="num">{fmtNum(r.qty)}</td><td className="money">{fmtINR(r.revenue)}</td></tr>
                ))
              )}</tbody>
            </table>
          </div>
        </div>

        <div className="card table-card">
          <h3>Top 10 Customers Sales</h3>
          <div className="table-scroll">
            <table className="mini">
              <colgroup>
                <col className="w-flex" /><col className="w-120" /><col className="w-96" /><col className="w-140" /><col className="w-140" />
              </colgroup>
              <thead>
                <tr><th className="left">Customer</th><th className="num">Phone</th><th className="num">Orders</th><th className="money">Revenue</th><th className="date">Last Purchase</th></tr>
              </thead>
              <tbody>{data.top_customers.map((r,i)=>(
                <tr key={i}><td className="left">{r.name}</td><td className="num nowrap">{r.phone || '—'}</td><td className="num">{fmtNum(r.orders)}</td><td className="money">{fmtINR(r.revenue)}</td><td className="date nowrap">{r.last_purchase}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Returns tables */}
      <div className="grid two-eq">
        <div className="card table-card">
          <h3>Top 10 Products Returns</h3>
          <div className="table-scroll">
            <table className="mini">
              <colgroup>
                <col className="w-flex" /><col className="w-120" /><col className="w-140" />
              </colgroup>
              <thead>
                <tr><th className="left">Product</th><th className="num">Qty Returned</th><th className="money">Amount</th></tr>
              </thead>
              <tbody>{data.returns_top_products.length === 0 ? (
                <tr><td colSpan={3} className="muted">No returns in this range.</td></tr>
              ) : (
                data.returns_top_products.map((r,i)=>(
                  <tr key={i}><td className="left">{r.name}</td><td className="num">{fmtNum(r.qty)}</td><td className="money">{fmtINR(r.amount)}</td></tr>
                ))
              )}</tbody>
            </table>
          </div>
        </div>

        <div className="card table-card">
          <h3>Top 10 Customers Returns</h3>
          <div className="table-scroll">
            <table className="mini">
              <colgroup>
                <col className="w-flex" /><col className="w-120" /><col className="w-96" /><col className="w-140" /><col className="w-140" />
              </colgroup>
              <thead>
                <tr><th className="left">Customer</th><th className="num">Phone</th><th className="num">Returns</th><th className="money">Amount</th><th className="date">Last Return</th></tr>
              </thead>
              <tbody>{data.returns_top_customers.length === 0 ? (
                <tr><td colSpan={5} className="muted">No returns in this range.</td></tr>
              ) : (
                data.returns_top_customers.map((r,i)=>(
                  <tr key={i}><td className="left">{r.customer}</td><td className="num nowrap">{r.phone||'—'}</td><td className="num">{fmtNum(r.returns)}</td><td className="money">{fmtINR(r.amount)}</td><td className="date nowrap">{r.last_return||'—'}</td></tr>
                ))
              )}</tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bottom pair: Credit + Sales by State */}
      <div className="grid two-eq">
        <div className="card table-card">
          <h3>Top Customers Store Credit</h3>
          <div className="table-scroll">
            <table className="mini">
              <colgroup>
                <col className="w-flex" /><col className="w-120" /><col className="w-140" />
              </colgroup>
              <thead><tr><th className="left">Customer</th><th className="num">Phone</th><th className="money">Credit</th></tr></thead>
              <tbody>{data.top_credit_customers.length === 0 ? (
                <tr><td colSpan={3} className="muted">No customers with credit.</td></tr>
              ) : (
                data.top_credit_customers.map((r,i)=>(
                  <tr key={i}><td className="left">{r.name}</td><td className="num nowrap">{r.phone||'—'}</td><td className="money">{fmtINR(r.credit)}</td></tr>
                ))
              )}</tbody>
            </table>
          </div>
        </div>

        <div className="card eq-card">
          <h3>Sales by State</h3>
          <div className="chart-fill" style={{ minHeight: 260, minWidth: 320, overflowX:'auto' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.sales_by_state} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="state" />
                <YAxis width={60} tickFormatter={(v)=>fmtINR(v).replace(/₹\\s?/,'₹')} />
                <Tooltip formatter={(v, n)=> n==='revenue'? fmtINR(v): v} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} /><Legend />
                <Bar dataKey="revenue" name="Revenue" fill="#6366f1" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {loading && (<div className="toast">Loading analytics…</div>)}

      <style>{`
        :root { --pairHeight: 360px; }

        .page{padding:16px; background: linear-gradient(135deg, #f8fafc 0%, #f0f9ff 100%); width:100%; box-sizing:border-box;}
        @media (max-width: 640px){ .page{padding:12px;} }
        .row{display:flex; align-items:center}
        .between{justify-content:space-between}
        .gap8{gap:8px}

        /* controls */
        .controls{flex-wrap:wrap; justify-content:flex-end; max-width:100%}
        .controls input[type="date"]{
          height:32px; padding:6px 8px; border:1px solid #e5e7eb; border-radius:8px;
          background:#fff; color:#111; font-weight:500;
        }
        .btn{
          display:inline-flex; align-items:center; justify-content:center;
          height:32px; padding:0 12px; border:1px solid #3b82f6; border-radius:8px;
          background:#3b82f6; color:#fff; font-weight:600; cursor:pointer; white-space:nowrap;
          transition: all 0.2s ease;
        }
        .btn:hover{background:#2563eb; border-color:#2563eb; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(37,99,235,0.25)}
        .btn:active{transform:translateY(0px)}
        @media (max-width: 640px){
          .controls{justify-content:flex-start; gap:8px;}
          .controls input[type="date"]{flex:1; min-width:0; width:100%;}
          .controls .btn{flex:1;}
        }

        /* header */
        .head-left h2{ margin:0; color:#1f2937; font-size: 28px; }
        .subhead{ margin-top:4px; font-size:14px; color:#6b7280; font-weight:500; }

        .kpi-grid{display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:12px; margin:12px 0}
        @media (max-width: 1024px){ .kpi-grid{grid-template-columns:repeat(2,minmax(0,1fr))} }
        @media (max-width: 640px){ .kpi-grid{grid-template-columns:1fr} }
        
        .kpi{
          border-radius:12px; padding:16px; font-weight:500;
          border: none; position: relative; overflow: hidden;
          color: #fff; box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          transition: all 0.3s ease;
        }
        .kpi:hover{ transform: translateY(-4px); box-shadow: 0 8px 20px rgba(0,0,0,0.12); }
        .kpi-1{ background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); }
        .kpi-2{ background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
        .kpi-3{ background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
        .kpi-4{ background: linear-gradient(135deg, #ec4899 0%, #be185d 100%); }
        .kpi-5{ background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
        .kpi-6{ background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); }
        .kpi-7{ background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
        .kpi-8{ background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); }
        
        .kpi .label{ font-size: 12px; opacity: 0.9; font-weight:500; margin-bottom: 6px; }
        .kpi .meta{ font-size: 11px; opacity: 0.8; margin-top: 4px; }
        .kpi .muted{ opacity: 0.9; }
        
        .muted{color:#6b7280}
        .tiny{font-size:12px}
        .big{font-size:28px; font-weight:700; letter-spacing: -0.5px;}
        .grid{display:grid; gap:12px; margin:12px 0}
        .two-eq{grid-template-columns:1fr 1fr}
        @media (max-width: 1024px){ .two-eq{grid-template-columns:1fr} }
        .card{background:#fff; border:1px solid #e5e7eb; border-radius:12px; padding:16px; box-shadow: 0 2px 8px rgba(0,0,0,0.05);}
        .card h3{ margin-top: 0; color: #1f2937; font-size: 16px; }
        .h-300{height:300px}
        .badge{font-size:12px; padding:6px 12px; border-radius:8px; border:1px solid #ddd; background:#f8fafc; color:#1f2937; font-weight:600;}

        /* Donuts with vertical separator */
        .donut-grid{
          display:grid;
          grid-template-columns: 1fr auto 1fr; /* left | sep | right */
          gap:16px;
          align-items:stretch;
        }
        .v-sep{ width:2px; background: linear-gradient(to bottom, transparent, #e5e7eb, transparent); border-radius:1px; }
        @media (max-width: 900px){
          .donut-grid{ grid-template-columns: 1fr; }
          .v-sep{ display:none; }
        }
        .donut-wrap{
          min-height: var(--pairHeight);
          display:flex; flex-direction:column;
          padding:8px 16px; background: #fff; border-radius: 12px; border: 1px solid #e5e7eb;
        }
        .donut-chart{ flex:1; }
        .donut-total-top { font-size: 12px; fill: #6b7280; }
        .donut-total { font-size: 16px; font-weight: 700; fill: #1f2937; }

        /* Summary rows under donuts */
        .gst-summary{
          display:grid; grid-template-columns: repeat(3,minmax(0,1fr));
          gap:8px; margin-top:8px; font-size:12px;
        }
        .gst-summary > div{
          display:flex; align-items:baseline; gap:6px; justify-content:center;
          background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
          border:1px solid #bbf7d0; border-radius:8px; padding:8px 8px;
          color: #065f46; font-weight: 600;
        }
        .gst-summary .pct{ color:#059669 }
        .gst-summary .only-center{ grid-column: span 1; justify-content:center }
        @media (max-width: 640px){
          .gst-summary{ grid-template-columns: 1fr; }
          .gst-summary .only-center{ grid-column:auto }
        }

        /* Equal-height cards for tables and bottom chart */
        .table-card{
          height: var(--pairHeight);
          display:flex; flex-direction:column;
          background: #fff; border-radius: 12px; border: 1px solid #e5e7eb;
          width:100%;
          box-sizing:border-box;
          min-width:0;
        }
        .eq-card{
          height: var(--pairHeight);
          display:flex; flex-direction:column;
          background: #fff; border-radius: 12px; border: 1px solid #e5e7eb;
          width:100%;
          box-sizing:border-box;
          overflow:hidden;
        }
        .eq-card > h3{ margin-bottom:8px; color: #1f2937; }
        .chart-fill{ flex:1; min-height:0; width:100%; box-sizing:border-box; overflow:hidden; }
        .chart-fill svg{max-width:100%;}

        .table-card > h3{ margin-bottom:8px; color: #1f2937; }
        .table-scroll{
          overflow:auto;
          border:1px solid #f4f4f5; border-radius:10px;
          flex:1;
          width:100%;
          box-sizing:border-box;
          padding-bottom:4px;
          scrollbar-gutter: stable; /* preserves space so header/columns don't shift */
        }
        .table-scroll table{ width:100%; }

        /* ====== TABLES (aligned columns) ====== */
        table.mini{width:100%; border-collapse:collapse; table-layout:fixed; font-variant-numeric: tabular-nums; }
        table.mini th, table.mini td{padding:8px; line-height:20px; vertical-align:middle; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
        table.mini thead th{ position:sticky; top:0; background: linear-gradient(135deg, #f0f9ff 0%, #f8fafc 100%); z-index:1; border-bottom:2px solid #e5e7eb; font-weight:600; color:#1f2937; }
        table.mini tbody tr{border-top:1px solid #f0f0f0}
        table.mini tbody tr:hover{ background: #fafafa; }
        /* column width helpers used by <colgroup> */
        col.w-flex{ width:auto; }
        col.w-96{ width:96px; }
        col.w-120{ width:120px; }
        col.w-140{ width:140px; }
        /* alignments */
        th.left, td.left{ text-align:left; }
        th.num, td.num{ text-align:right; }
        th.money, td.money{ text-align:right; }
        th.date, td.date{ text-align:right; }
        .nowrap{ white-space:nowrap; }
        @media (max-width: 720px){
          /* allow scroll but prefer wrapping for narrow screens */
          table.mini{
            table-layout:auto;
            min-width:0;
            width:100%;
          }
          table.mini th, table.mini td{
            white-space:normal;
            text-overflow:clip;
            padding:6px;
            font-size:13px;
          }
          .table-scroll{overflow-x:auto; -webkit-overflow-scrolling: touch;}
          .table-scroll::-webkit-scrollbar{height:6px}
          .table-scroll::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:999px}
          /* relax fixed col widths */
          col.w-96, col.w-120, col.w-140, col.w-flex{width:auto !important;}
        }

        .toast{position:fixed; bottom:16px; right:16px; background:#fff; border:2px solid #3b82f6; padding:12px 16px; border-radius:10px; box-shadow:0 8px 24px rgba(59,130,246,0.15); color:#1f2937; font-weight:600;}

        /* ==== HEATMAP (fills card height) ==== */
        .heatmap{display:flex; flex-direction:column; gap:8px; height:100%; width:100%; overflow-x:auto;}
        .hm-grid{
          display:grid;
          grid-template-columns:80px 1fr;
          grid-template-rows:auto repeat(7, 1fr); /* header + 7 equal rows */
          gap:4px;
          height:100%;
          flex:1;
          min-height:0;
        }
        .hm-x{
          display:grid;
          grid-template-columns:repeat(24, minmax(12px,1fr));
          gap:2px;
          align-items:center;
        }
        .hm-row{
          display:grid;
          grid-template-columns:repeat(24, minmax(12px,1fr));
          gap:2px;
          height:100%;
        }
        .hm-cell{height:100%; border-radius:4px; transition: all 0.2s ease; cursor: pointer;}
        .hm-cell:hover{ opacity: 0.8; }
        .hm-ylab{font-size:12px; color:#6b7280; display:flex; align-items:center; font-weight:600}
        .hm-xlab{font-size:10px; color:#9ca3af; text-align:center}
        .hm-legend{display:flex; align-items:center; gap:8px; font-size:11px; color:#6b7280; font-weight:500}
        .hm-grad{height:8px; width:140px; border-radius:6px; background:linear-gradient(to right, hsl(210 100% 50%), hsl(210 100% 90%))}

        @media (max-width: 720px){
          /* Charts & donuts scale down for phones */
          .eq-card{height:auto; min-height:240px;}
          .chart-fill{height:220px;}
          .chart-fill svg{width:100%!important; height:auto!important;}
          .donut-wrap{min-height:240px;}
          .donut-chart{height:220px!important; width:100%!important;}
          .donut-chart svg{width:100%!important; height:auto!important;}
          .heatmap{overflow-x:auto;}
          .hm-grid{min-width:520px;}
        }

        @media (max-width: 720px){
          /* Charts and donuts shrink/wrap on phones */
          .eq-card{height:auto; min-height:260px;}
          .chart-fill{height:260px;}
          .chart-fill svg{width:100%!important; height:auto!important;}
          .donut-wrap{min-height:260px;}
          .donut-chart{height:240px!important; width:100%!important;}
          .donut-chart svg{width:100%!important; height:auto!important;}
          .heatmap{overflow-x:auto;}
          .hm-grid{min-width:520px;}
          .h-300{min-width:360px;}
          .grid.two-eq > .card{overflow-x:auto;}
        }
      `}</style>
    </div>
  );
}
